import React, {
    useCallback,
    useEffect,
    useReducer,
    useRef,
    useState
} from 'react';
import classNames from 'classnames/bind';
import logoSmall from '../../Assets/logo192.png';
import logoBig from '../../Assets/logo512.png';
import { AppCommandBar } from '../../Components/AppCommandBar/AppCommandBar';
import MsalAuthService, {
    DEFAULT_TENANT_ID
} from '../../Services/MsalAuthService';
import { Stack } from '@fluentui/react';
import {
    Route,
    Routes,
    useLocation,
    useNavigationType,
    NavigationType
} from 'react-router-dom';
import {
    AdtExplorerTenantIdStorageKey,
    BlobContainerURLParamKey,
    ConfigurationStorageKey,
    EnvironmentIdStorageKey,
    EnvironmentIdURLParamKey,
    EnvironmentsStorageKey,
    ScenesFreHasBeenSeenKey,
    TenantIdURLParamKey
} from '../../Models/Constants/Constants';
import {
    getLocalStorageJSONItem,
    getSecureUrlObjFromString,
    getUrlParam,
    setLocalStorageJSONArrayItem
} from '../../Services/Utils';
import HamburgerMenu from '../../Components/HamburgerMenu/HamburgerMenu';
import { SettingsPanel } from '../../Components/SettingsPanel/SettingsPanel';
import {
    ADTConfigurationInLocalStorage,
    ADTEnvironmentInLocalStorage,
    AppContextType
} from '../../Models/Constants/Types';
import { AppReducer, defaultAppState } from './App.state';
import {
    LOGIN_SUCCESS,
    SET_IS_LOADING,
    SET_IS_SETTINGS_PANEL_OPEN,
    SET_IS_USER_MENU_EXTENDED
} from '../../Models/Constants/ActionTypes';
import { UserMenu } from '../../Components/UserMenu/UserMenu';
import { useTranslation } from 'react-i18next';
import { AppPage } from '../../Models/Constants/Enums';
import usePrevious from '../../Models/Hooks/usePrevious';
import Scene3D from '../Scene3D/Scene3D';
import ToolsRouter from '../Tools/ToolsRouter';
// import Scene3DDemo from '../Scene3DDemo/Scene3DDemo';
import OntologyDesigner from '../OntologyDesigner/OntologyDesigner';
import styles from './App.module.scss';
const cx = classNames.bind(styles);

export const AppContext = React.createContext<AppContextType>({
    selectedAdtUrl: undefined,
    setSelectedAdtUrl: (value: string | undefined) => {},
    activePage: AppPage.Scene3D,
    setActivePage: (value: AppPage) => {},
    currentUserUniqueObjectId: '',
    is3DScenesSplashVisible: false,
    setIs3DScenesSplashVisible: (value: boolean) => {}
});

const validAdtHostSuffixes = [
    '.digitaltwins.azure.net',
    '.pp.azuredigitaltwins-ppe.net',
    '.azuredigitaltwins-test.net'
];
const getSanitizedEnvironmentUrl = (eid: string | null) => {
    const getUrlFromString = (urlString: string) => {
        try {
            let urlStr = urlString;
            if (
                !(urlStr.startsWith('https://') || urlStr.startsWith('http://'))
            ) {
                urlStr = 'https://' + urlStr;
            }
            return new URL(urlStr);
        } catch (error) {
            console.error('Failed to get url from string');
            return null;
        }
    };
    if (eid) {
        // remove any unexpected characters
        const url = getUrlFromString(eid);
        if (
            validAdtHostSuffixes.some((suffix) =>
                url?.hostname.endsWith(suffix)
            )
        ) {
            return url?.href;
        } else {
            console.error('Invalid environment URL in query parameter');
        }
    }

    return null;
};

const getEnvironment = () => {
    const eid =
        getUrlParam(EnvironmentIdURLParamKey) ||
        (
            getLocalStorageJSONItem(
                ConfigurationStorageKey
            ) as ADTConfigurationInLocalStorage
        )?.[EnvironmentIdStorageKey];
    return getSecureUrlObjFromString(getSanitizedEnvironmentUrl(eid))?.href;
};

const getActivePageFromUrl = () => {
    let activePage = AppPage.Scene3D;
    const pathName = new URL(window.location.href).pathname?.toLowerCase();
    if (
        pathName === '/tools/data-pusher' ||
        pathName === '/tools/data-pusher/'
    ) {
        activePage = AppPage.DataPusher;
    } else if (pathName === '/3dscenes' || pathName === '/3dscenes/') {
        activePage = AppPage.Scene3D;
    } else if (pathName === '/tools' || pathName === '/tools/') {
        activePage = AppPage.Tools;
    } else if (
        pathName === '/ontology-designer' ||
        pathName === '/ontology-designer/'
    ) {
        activePage = AppPage.OntologyDesigner;
    } else if (pathName === '/') {
        activePage = AppPage.Graphs;
    }
    return activePage;
};

const App: React.FC = () => {
    // adtUrl, activePage and urlParams are used for contexts to be shared among components, that is why they are not controlled by appReducer
    const [adtUrl, setAdtUrl] = useState<string | undefined>(getEnvironment());
    const [activePage, setActivePage] = useState(getActivePageFromUrl());
    const [userUniqueObjectId, setUserUniqueObjectId] = useState<string>('');
    const [isAuthNecessary, setIsAuthNecessary] = useState<boolean>(true);
    const [is3DScenesSplashVisible, setIs3DScenesSplashVisible] =
        useState<boolean>(
            activePage === AppPage.Scene3D &&
                localStorage.getItem(ScenesFreHasBeenSeenKey) === null
        );
    const [state, dispatch] = useReducer(AppReducer, defaultAppState);
    const { t } = useTranslation();
    const location = useLocation();
    const shouldAddUrlToHistoryStack = useRef(true);
    const navigationType = useNavigationType();

    const appContextValue: AppContextType = {
        selectedAdtUrl: adtUrl,
        setSelectedAdtUrl: setAdtUrl,
        activePage,
        setActivePage,
        currentUserUniqueObjectId: userUniqueObjectId,
        is3DScenesSplashVisible: is3DScenesSplashVisible,
        setIs3DScenesSplashVisible: setIs3DScenesSplashVisible
    };

    const setTenantId = (tenantId: string) => {
        localStorage.setItem(AdtExplorerTenantIdStorageKey, tenantId);
    };

    const getTenantId = useCallback((tenants: any, isMsaUser: boolean) => {
        let tenantId = tenants.length ? tenants[0].tenantId : DEFAULT_TENANT_ID;
        const tenantIdFromUrlParam = getUrlParam(TenantIdURLParamKey);
        const tenantIdFromUrlParamInTenants =
            isMsaUser ||
            tenants.find(
                (tenant: any) => tenant.tenantId === tenantIdFromUrlParam
            );
        const tenantIdFromLocalStorage = localStorage.getItem(
            AdtExplorerTenantIdStorageKey
        );
        const tenantIdFromLocalStorageInTenants =
            isMsaUser ||
            tenants.find(
                (tenant: any) => tenant.tenantId === tenantIdFromLocalStorage
            );
        if (tenantIdFromUrlParam && tenantIdFromUrlParamInTenants) {
            tenantId = tenantIdFromUrlParam;
            localStorage.setItem(AdtExplorerTenantIdStorageKey, tenantId);
        } else {
            if (tenantIdFromLocalStorage && tenantIdFromLocalStorageInTenants) {
                tenantId = tenantIdFromLocalStorage;
            }
        }
        return tenantId;
    }, []);

    useEffect(() => {
        if (!state.isUserLoggedIn) {
            // Authentication onmount
            setIsAuthNecessary(true);
            dispatch({
                type: SET_IS_LOADING,
                payload: true
            });
            MsalAuthService.login(
                (
                    name: any,
                    userName: any,
                    tenants: any,
                    accounts: any,
                    isMsaUser = false
                ) => {
                    const tenantId = getTenantId(tenants, isMsaUser);
                    MsalAuthService.tenantId = tenantId;
                    setTenantId(tenantId);
                    const oId = accounts.find(
                        (a: any) => a.tenantId === tenantId
                    )?.idTokenClaims.oid;
                    setUserUniqueObjectId(oId);
                    // Updates obf userEmail to logged-in msal user's email
                    (
                        window as any
                    ).OfficeBrowserFeedback.initOptions.userEmail = userName;
                    const ssoContinuation = () => {
                        dispatch({
                            type: LOGIN_SUCCESS,
                            payload: {
                                userName: name || userName,
                                userUniqueObjectId: oId,
                                tenants: tenants,
                                selectedTenantId: tenantId
                            }
                        });
                        setTimeout(
                            () =>
                                dispatch({
                                    type: SET_IS_LOADING,
                                    payload: false
                                }),
                            2000
                        );
                    };
                    MsalAuthService.ssoSilent(userName, ssoContinuation);
                }
            );
        }
    }, [getTenantId, activePage, state.isUserLoggedIn]);

    const prevSelectedAdtUrl = usePrevious(appContextValue.selectedAdtUrl);
    useEffect(() => {
        const environmentsInLocalStorage =
            getLocalStorageJSONItem(EnvironmentsStorageKey) || [];
        const isEnvironmentInLocalStorage =
            environmentsInLocalStorage.findIndex(
                (e: ADTEnvironmentInLocalStorage) =>
                    e.config.appAdtUrl === appContextValue.selectedAdtUrl
            ) !== -1;
        if (!isEnvironmentInLocalStorage) {
            environmentsInLocalStorage.push({
                name: appContextValue.selectedAdtUrl,
                config: { appAdtUrl: appContextValue.selectedAdtUrl }
            } as ADTEnvironmentInLocalStorage);
            setLocalStorageJSONArrayItem(
                EnvironmentsStorageKey,
                environmentsInLocalStorage,
                true
            );
        }

        appContextValue.selectedAdtUrl !== null &&
            localStorage.setItem(
                ConfigurationStorageKey,
                JSON.stringify({
                    [EnvironmentIdStorageKey]: appContextValue.selectedAdtUrl
                })
            );
    }, [appContextValue.selectedAdtUrl, prevSelectedAdtUrl]);

    useEffect(() => {
        if (navigationType === NavigationType.Pop) {
            if (
                location.pathname === '/tools/data-pusher' ||
                location.pathname === '/tools/data-pusher/'
            ) {
                setActivePage(AppPage.DataPusher);
            } else if (
                location.pathname?.toLowerCase() === '/3dscenes' ||
                location.pathname?.toLowerCase() === '/3dscenes/'
            ) {
                setActivePage(AppPage.Scene3D);
                shouldAddUrlToHistoryStack.current = false;
                const searchParams = new URLSearchParams(location.search);
                let eidFromUrl: any = searchParams.get(
                    EnvironmentIdURLParamKey
                );
                let containerFromUrl: any = searchParams.get(
                    BlobContainerURLParamKey
                );
                if (eidFromUrl) {
                    eidFromUrl = getSecureUrlObjFromString(eidFromUrl)?.href;
                }
                if (containerFromUrl) {
                    containerFromUrl =
                        getSecureUrlObjFromString(containerFromUrl)?.href;
                }
            } else if (
                location.pathname === '/tools' ||
                location.pathname === '/tools/'
            ) {
                setActivePage(AppPage.Tools);
            } else if (
                location.pathname?.toLowerCase() === '/ontology-designer' ||
                location.pathname?.toLowerCase() === '/ontology-designer/'
            ) {
                setActivePage(AppPage.OntologyDesigner);
            } else {
                setActivePage(AppPage.Scene3D);
                shouldAddUrlToHistoryStack.current = false;
                const searchParams = new URLSearchParams(location.search);
                let eidFromUrl: any = searchParams.get(
                    EnvironmentIdURLParamKey
                );
                if (eidFromUrl) {
                    eidFromUrl = getSecureUrlObjFromString(eidFromUrl)?.href;
                }
            }
        }
    }, [location, navigationType]);

    return (
        <AppContext.Provider value={appContextValue}>
            {state.isLoading && (
                <div
                    className={cx(
                        'spinWrap',
                        state.isUserLoggedIn || !isAuthNecessary
                            ? 'fadeOut'
                            : ''
                    )}
                >
                    <img
                        alt="logoSpin"
                        className={cx('logoSpin')}
                        src={logoBig}
                    />
                </div>
            )}
            {(state.isUserLoggedIn || !isAuthNecessary) && (
                <div className={cx('main-grid')}>
                    <div className={cx('header')}>
                        <Stack horizontal className={cx('top-bar')}>
                            <div>
                                <img
                                    src={logoSmall}
                                    width={20}
                                    height={20}
                                    alt="appLogo"
                                />
                                <span className={cx('top-bar-title')}>
                                    {'Azure Digital Twins'}
                                    <span className={cx('title-preview')}>
                                        {t('preview')}
                                    </span>
                                </span>
                            </div>
                            <AppCommandBar
                                userName={state.userName}
                                onToggleUserMenu={() => {
                                    dispatch({
                                        type: SET_IS_USER_MENU_EXTENDED,
                                        payload: !state.isUserMenuExpanded
                                    });
                                }}
                                onSettingsClick={() => {
                                    dispatch({
                                        type: SET_IS_SETTINGS_PANEL_OPEN,
                                        payload: !state.isSettingsPanelOpen
                                    });
                                }}
                            />
                            <UserMenu
                                isUserMenuExpanded={state.isUserMenuExpanded}
                                setTenantId={setTenantId}
                                tenants={state.tenants}
                                selectedTenantId={state.selectedTenantId}
                            />
                        </Stack>
                    </div>
                    <main>
                        <HamburgerMenu />
                        <SettingsPanel
                            isOpen={state.isSettingsPanelOpen}
                            onDismiss={() =>
                                dispatch({
                                    type: SET_IS_SETTINGS_PANEL_OPEN,
                                    payload: !state.isSettingsPanelOpen
                                })
                            }
                        />
                        <div className={cx('page')}>
                            <div className={cx('rhs')}>
                                <Routes>
                                    <Route
                                        path="/3DScenes"
                                        element={<Scene3D />}
                                    />
                                    <Route
                                        path="/tools/*"
                                        element={<ToolsRouter />}
                                    />
                                    <Route
                                        path="/ontology-designer"
                                        element={<OntologyDesigner />}
                                    />
                                </Routes>
                            </div>
                        </div>
                    </main>
                </div>
            )}
        </AppContext.Provider>
    );
};

export default App;
